import {ActivatedRouteSnapshot, Resolve, Router} from '@angular/router';
import {catchError, map, take} from 'rxjs/operators';
import {of} from 'rxjs';
import {ApiService, RequestOptions} from './api.service';

/**
 * Базовый резолвер данных, который кеширует полученные данные
 * и в следующий раз возвращает их без необходимости повторного запроса к серверу.
 */

/**
 * Гайд https://angular.io/guide/router#resolve-pre-fetching-component-data
 */
export class BaseResolverService implements Resolve<any> {
    /**
     * Куда редиректить, если запрос прошел корректно, но запрашиваемых данных нет
     * @type {string}
     */
    protected redirectUrl: string;

    /**
     * Тут хранятся закешированные полученные данные, если кеш включен.
     * @type {null}
     */
    protected resolvedData = null;

    /**
     * Кешировать ли полученные данные, чтобы в дальнейшем не делать запросы к API
     * @type {boolean}
     */
    protected cacheData = false;

    constructor(
        protected resolverService: ApiService,
        protected resolverRouter: Router,
    ) {}

    resolve(snapshot: ActivatedRouteSnapshot) {
        /**
         * Если уже есть кешиированные данные, то просто их возвращаем.
         */
        if (this.cacheData && this.resolvedData) {
            return of(this.resolvedData);
        }

        /**
         * На случай ошибок в this.getRequestParams(snapshot)
         */
        try {
            return this.resolverService.get(this.getRequestParams(snapshot))
                .pipe(
                    take(1),
                    map(data => {
                        if (data) {
                            const processedData = this.processResolvedData(data);
                            if (this.cacheData) {
                                this.resolvedData = processedData;
                            }
                            return processedData;
                        } else {
                            return this.processNullData();
                        }
                    }),
                    catchError(() => {
                        return of(this.processError());
                    }),
                );
        } catch (e) {
            console.error(e);
            return this.processError();
        }
    }

    getRequestParams(snapshot: ActivatedRouteSnapshot): RequestOptions {
        const resolveFormData = snapshot.data['resolveAsForm'] || false;

        /**
         * Устанавливаем id для сервиса, если он нужен
         */
        const { id } = snapshot.params;

        if (resolveFormData) {
            return {
                url: this.resolverService.getFormDataUrl(id)
            };
        } else {
            return {
                id: id
            };
        }
    }

    /**
     * Преобразовать полученные данные в нужный вид.
     *
     * Метод необходим для тех случаев, когда в унаследованном сервисе данные нужно как-то видоизменить,
     * прежде чем закешировать и вернуть результат.
     * @param data
     * @returns {any}
     */
    processResolvedData(data) {
        return data;
    }

    /**
     * Обработать null данные.
     * Метод можно переопределить в дочерних резолверах и сделать, например, редиректы
     */
    processNullData(): any {
        this.redirect();
    }

    /**
     * Обработать ошибку получения данных.
     * Метод можно переопределить в дочерних резолверах и сделать, например, редиректы
     */
    processError() {
        this.redirect();
    }

    protected redirect() {
        this.resolverRouter.navigate(this.redirectUrl ? [this.redirectUrl] : ['/']);
        return null;
    }
}
